import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dataGridTextSorter } from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { Spinner } from '../../../../common/index.jsx';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import CollapseBlock from '../../../../common/blocks/CollapseBlock/CollapseBlock.jsx';
import PropTypes from 'prop-types';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import { CheckOutlined, CloseOutlined, LikeOutlined } from '@ant-design/icons';
import { useDataGridDateRangeFilter } from '../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import { Badge, Flex, Radio, Tag } from 'antd';
import { SeriousDeleteQuestion } from '../../../../common/DeleteQuestion/SeriousDeleteQuestion.jsx';

const ExternalPartnersTable = ({
  partnersData,
  onDeleteClick,
  onEditClick,
  onApproveClick,
  onActivateClick,
  onDeactivateClick,
}) => {
  const { t } = useTranslation();

  const [selectedPartnersListOption, setSelectedPartnersListOption] = useState(
    () => {
      return sessionStorage.getItem('extPartnersTypeOption') !== null
        ? sessionStorage.getItem('extPartnersTypeOption')
        : 'approved';
    },
  );

  const filteredData = useMemo(() => {
    switch (selectedPartnersListOption) {
      case 'approved':
        return partnersData.filter(
          (partner) => partner.active && partner.approved_at,
        );
      case 'waiting':
        return partnersData.filter(
          (partner) => partner.active && !partner.approved_at,
        );
      case 'inactive':
        return partnersData.filter((partner) => !partner.active);
      default: {
        return [];
      }
    }
  }, [partnersData, selectedPartnersListOption]);

  const partnersLisOptionText = {
    approved: t('Zoznam schválených externých partnerov FRI'),
    waiting: t('Zoznam externých partnerov FRI čakajúcich na schválenie'),
    inactive: t('Zoznam neaktívnych externých partnerov FRI'),
  };

  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount(
      partnersData.filter((partner) => partner.active && !partner.approved_at)
        .length,
    );
  }, [filteredData]);

  const handleRadioChange = (e) => {
    setSelectedPartnersListOption(e.target.value);
    sessionStorage.setItem('extPartnersTypeOption', e.target.value);
  };

  // Custom settings
  const customSettings = (row) => {
    return (
      <>
        {selectedPartnersListOption === 'waiting' && (
          <IconButton
            icon={<LikeOutlined />}
            tooltip={t('Schváliť')}
            onClick={(e) => {
              e.stopPropagation();
              onApproveClick(row);
            }}
          />
        )}
        {selectedPartnersListOption === 'inactive' && (
          <IconButton
            icon={<CheckOutlined />}
            tooltip={t('Povoliť prístup')}
            onClick={(e) => {
              e.stopPropagation();
              onActivateClick(row);
            }}
          />
        )}
        {selectedPartnersListOption === 'approved' && (
          <IconButton
            icon={<CloseOutlined />}
            tooltip={t('Zrušiť prístup')}
            onClick={(e) => {
              e.stopPropagation();
              onDeactivateClick(row);
            }}
          />
        )}
      </>
    );
  };

  // Sorters
  const partnerNameTextFilterProps = useDataGridTextFilter('name');
  const partnerContactPersonTextFilterProps = useDataGridTextFilter([
    'contact_person',
    'full_name',
  ]);
  const partnerContactMailTextFilterProps = useDataGridTextFilter([
    'contact_person',
    'email',
  ]);
  const partnerApprovedAtDateFilterProps =
    useDataGridDateRangeFilter('approved_at');

  // Columns
  const dataGridColumns = useMemo(
    () => [
      {
        title: t('Názov firmy'),
        dataIndex: 'name',
        ellipsis: true,
        sorter: dataGridTextSorter('name'),
        defaultSortOrder: 'ascend',
        ...partnerNameTextFilterProps,
      },
      {
        title: t('Kontaktná osoba'),
        dataIndex: ['contact_person', 'full_name'],
        ellipsis: true,
        sorter: dataGridTextSorter('contact_full_name'),
        ...partnerContactPersonTextFilterProps,
      },
      {
        title: t('E-mail'),
        dataIndex: ['contact_person', 'email'],
        ellipsis: true,
        sorter: dataGridTextSorter('contact_email'),
        ...partnerContactMailTextFilterProps,
      },
      {
        title: t('Schválený dňa'),
        dataIndex: 'approved_at',
        ellipsis: true,
        sorter: dataGridTextSorter('approved_at'),
        render: (date) =>
          (date && new Date(date).toLocaleString('sk-SK')) || '-',
        ...partnerApprovedAtDateFilterProps,
      },
      {
        title: t('Status'),
        dataIndex: 'state',
        ellipsis: true,
        render: (status) => (
          <Tag
            color={
              status === 'Schválený'
                ? 'green'
                : status === 'Čaká na schválenie'
                  ? 'orange'
                  : 'red'
            }
          >
            {status}
          </Tag>
        ),
      },
    ],
    [t],
  );

  return !filteredData ? (
    <Spinner />
  ) : (
    <>
      <div>
        <CollapseBlock
          collapsible={true}
          title={t('Filter externých partnerov FRI')}
        >
          <Flex style={{ alignItems: 'baseline' }} gap="middle">
            <Flex vertical gap="middle">
              <Radio.Group
                block
                optionType="button"
                value={selectedPartnersListOption}
                onChange={handleRadioChange}
                style={{ whiteSpace: 'nowrap' }}
              >
                <Radio value="approved">
                  {partnersLisOptionText['approved']}
                </Radio>
                <Radio value="waiting">
                  {partnersLisOptionText['waiting']}
                  <Badge
                    count={count}
                    showZero
                    size="small"
                    overflowCount={99}
                    offset={[0, -15]}
                    color={count ? 'orange' : 'green'}
                  ></Badge>
                </Radio>
                <Radio value="inactive">
                  {partnersLisOptionText['inactive']}
                </Radio>
              </Radio.Group>
            </Flex>
          </Flex>
        </CollapseBlock>
        <CollapseBlock
          collapsible={true}
          title={partnersLisOptionText[selectedPartnersListOption]}
        >
          <DataGrid
            rowKey={(partner) => partner.id}
            columns={dataGridColumns}
            dataSource={filteredData || []}
            permissionsBasic={'external-partners'}
            uniqueTableId={'external-partners-table'}
            onDelete={onDeleteClick}
            onEditClick={onEditClick}
            onRowClick={onEditClick}
            customSettings={customSettings}
            question={
              <SeriousDeleteQuestion
                firstText={t(
                  'Naozaj chcete vymazať tohto externého partnera FRI?',
                )}
                mainNotice={t(
                  'Spolu s tým sa vymažú všetky pracovné ponuky a ostatné údaje.',
                )}
                lastText={t(
                  'Prosím uistite sa, že naozaj chcete vymazať externého partnera FRI.',
                )}
              />
            }
          />
        </CollapseBlock>
      </div>
    </>
  );
};

ExternalPartnersTable.propTypes = {
  partnersData: PropTypes.array,
  onDetailClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onApproveClick: PropTypes.func,
  onActivateClick: PropTypes.func,
  onDeactivateClick: PropTypes.func,
};

export default ExternalPartnersTable;
